import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Radio,
  RadioGroup,
  Badge,
  Spinner,
  Box,
  Alert,
  AlertIcon,
  useToast
} from '@chakra-ui/react';
import { FaCloudDownloadAlt } from 'react-icons/fa';
import { verifyCurrentUser, loadSaveSlotsSafe } from '../../services/userSettingsService';

// Helper function for formatting dates
const formatDateRelative = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMins < 5) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

const LoadSlotModal = ({ isOpen, onClose, onLoad, slotData, isLoading, setSlotData, accentColor }) => {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [internalLoading, setInternalLoading] = useState(false);
  const toast = useToast();
  
  // Select active slot when data changes
  useEffect(() => {
    if (slotData?.save_slots) {
      const activeSlot = slotData.save_slots.find(slot => slot.active);
      if (activeSlot) {
        setSelectedSlot(activeSlot.slot);
      } else if (slotData.save_slots.some(slot => slot.settings)) {
        // If no active slot but there are saved slots, select the first non-empty one
        const firstNonEmpty = slotData.save_slots.find(slot => slot.settings);
        if (firstNonEmpty) {
          setSelectedSlot(firstNonEmpty.slot);
        }
      }
    }
  }, [slotData]);

  // Verify user and refresh data when modal opens
  useEffect(() => {
    if (isOpen) {
      setInternalLoading(true);
      
      verifyCurrentUser().then(async result => {
        if (!result.valid) {
          toast({
            title: "Session Error",
            description: "Your login session has expired. Please refresh the page.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          onClose();
          setInternalLoading(false);
          return;
        } 
        
        if (result.changed) {
          toast({
            title: "User Changed",
            description: "Detected a different user. Loading your saved configurations...",
            status: "info",
            duration: 3000,
            isClosable: true,
          });
          
          // Reload the data when user changes
          try {
            const freshData = await loadSaveSlotsSafe();
            if (freshData.success && setSlotData) {
              setSlotData(freshData.slotData);
            }
          } catch (error) {
            console.error("Error refreshing data after user change:", error);
            toast({
              title: "Error Loading Data",
              description: "Could not load your saved configurations. Please try again.",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          }
        }
        
        setInternalLoading(false);
      }).catch(error => {
        console.error("Error verifying user:", error);
        setInternalLoading(false);
        toast({
          title: "Authentication Error",
          description: "Could not verify your account. Please refresh and try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
    }
  }, [isOpen, toast, onClose, setSlotData]);

  // Handle loading the selected slot
  const handleLoad = async () => {
    if (!selectedSlot) return;
    
    setVerifying(true);
    
    try {
      // Verify current user before loading
      const userStatus = await verifyCurrentUser();
      
      if (!userStatus.valid) {
        toast({
          title: "Authentication Error",
          description: "Please refresh the page and try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      
      // If user changed, we need fresh data before proceeding
      if (userStatus.changed) {
        toast({
          title: "User Session Changed",
          description: "Refreshing your data...",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        
        const freshData = await loadSaveSlotsSafe();
        if (freshData.success && setSlotData) {
          setSlotData(freshData.slotData);
        }
      }
      
      onLoad(selectedSlot);
    } catch (error) {
      console.error("Error in handleLoad:", error);
      toast({
        title: "Error",
        description: "An error occurred while loading your configuration.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setVerifying(false);
    }
  };

  const hasAnySavedSlots = slotData?.save_slots?.some(slot => slot.settings);
  const isButtonDisabled = isLoading || verifying || internalLoading || 
                          !selectedSlot || 
                          !slotData?.save_slots?.find(slot => slot.slot === selectedSlot)?.settings;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="md"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color={accentColor}>Load Widget Configuration</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {internalLoading && (
            <Box display="flex" justifyContent="center" mb={4}>
              <Spinner size="md" color={accentColor} />
            </Box>
          )}
          
          {!internalLoading && !hasAnySavedSlots && (
            <Alert status="info" mb={4}>
              <AlertIcon />
              No saved configurations found. Save your configurations first.
            </Alert>
          )}
          
          <Text mb={4}>
            Select a configuration to load. This will change your current editor settings.
          </Text>
          
          <RadioGroup onChange={(val) => setSelectedSlot(parseInt(val))} value={selectedSlot?.toString()}>
            <VStack spacing={3} align="start" width="100%">
              {slotData?.save_slots ? (
                slotData.save_slots.map((slot) => (
                  <Box
                    key={`slot-${slot.slot}`}
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    width="100%"
                    borderColor={selectedSlot === slot.slot ? accentColor : "gray.200"}
                    bg={selectedSlot === slot.slot ? `${accentColor}10` : "transparent"}
                    opacity={slot.settings ? 1 : 0.5}
                  >
                    <HStack justifyContent="space-between">
                      <Radio 
                        value={slot.slot.toString()} 
                        colorScheme="green"
                        isDisabled={!slot.settings}
                      >
                        <Text fontWeight="medium">{slot.name || `Slot ${slot.slot}`}</Text>
                      </Radio>
                      <HStack>
                        {slot.active && (
                          <Badge colorScheme="green">Active</Badge>
                        )}
                        {slot.last_saved && (
                          <Text fontSize="xs" color="gray.500">
                            {formatDateRelative(slot.last_saved)}
                          </Text>
                        )}
                      </HStack>
                    </HStack>
                    
                    {!slot.settings && (
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Empty slot
                      </Text>
                    )}
                  </Box>
                ))
              ) : (
                !internalLoading && <Spinner size="sm" />
              )}
            </VStack>
          </RadioGroup>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button 
            colorScheme="purple" 
            leftIcon={(isLoading || verifying || internalLoading) ? <Spinner size="sm" /> : <FaCloudDownloadAlt />}
            onClick={handleLoad}
            isDisabled={isButtonDisabled}
          >
            {isLoading || verifying || internalLoading ? "Loading..." : "Load"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LoadSlotModal;